import { useEffect, useState } from "react";
import {
    api,
    defaultPolicies,
    gamePath,
    housePath,
    parseRoute,
    roomPath,
    type CatalogGame,
    type Game,
    type Guest,
    type Home,
    type House,
    type Room,
} from "./api";
import {
    Avatar,
    Dialog,
    Field,
    GameShield,
    RoomNameBuilder,
    SectionTitle,
} from "./GuildhallParts";
import { AccountPanel } from "./AccountPanel";
import { GameSetup, type LaunchConfig } from "./GameSetup";
import { BotTypeFields, botChoiceValid } from "./BotPicker";
import { botRequest, botSubtitle, describeBotParams, selectableBots, type BotChoice } from "./botTypes";
import { enabledSettings } from "./setup";
import { GameCharter, HouseCharter, RoomCharter } from "./Charter";
import { PerspectiveReview } from "./PerspectiveReview";
import { PublicResult, resultSummary } from "./PublicResult";
import { PlayerGate } from "./Clubhouse";
import { IssueReporter } from "../issues/IssueReporter";
import { takeCredential } from "../session/fragment";
import { withLayout } from "../presentation/variants";
import "../styles/guildhall.css";
import "../styles/workspace.css";
import { WorkspaceBrowser, WorkspaceSidebar, RoomPicker, HouseConnections, type WorkspaceHome, type WorkspaceView } from "./Workspace";
import type { BrowsePage } from "./useBrowse";
const navigate = (path: string) => {
    window.location.href = path;
};
type Modal =
    | "account"
    | "room"
    | "setup"
    | "room-settings"
    | "house-settings"
    | "game-settings"
    | "invite"
    | "favorite-room"
    | null;
export function Guildhall({
    guest,
    onGuest,
    recovery,
    onRecovery,
    initialCredential,
}: {
    guest: Guest | null;
    onGuest: (g: Guest | null) => void;
    recovery: string | null;
    onRecovery: (value: string | null) => void;
    initialCredential: ReturnType<typeof takeCredential>;
}) {
    const route = parseRoute(window.location.pathname);
    const [house, setHouse] = useState<House | null>(null);
    const [room, setRoom] = useState<Room | null>(null);
    const [game, setGame] = useState<Game | null>(null);
    const [home, setHome] = useState<WorkspaceHome | null>(null);
    const [catalog, setCatalog] = useState<CatalogGame[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [online, setOnline] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rematchPresetMissingFor, setRematchPresetMissingFor] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [modal, setModal] = useState<Modal>(null);
    const [initialGame, setInitialGame] = useState<string | undefined>();
    const [roomName, setRoomName] = useState("");
    const [credential, setCredential] = useState(initialCredential);
    const rpath = route.roomId ? roomPath(route.roomId) : "";
    const gpath =
        route.roomId && route.gameId
            ? gamePath(route.roomId, route.gameId)
            : "";
    const [refreshKey, setRefreshKey] = useState(0);
    const requestedView = new URLSearchParams(window.location.search).get("view");
    const workspaceView: WorkspaceView = ["games","rooms","houses","archive","catalog","favorites","invitations"].includes(requestedView ?? "") ? requestedView as WorkspaceView : "games";
    const refresh = async () => {
        try {
            const catalogPromise = api<BrowsePage<CatalogGame>>("/browse/catalog?pageSize=60").then(
                (value) => ({ value }),
                (error) => ({ error }),
            );
            if (route.roomId) {
                let nextRoom = await api<Room>(`${rpath}?bounded=true`);
                if (
                    guest &&
                    !nextRoom.admitted &&
                    nextRoom.permissions?.enter !== false
                )
                    nextRoom = await api<Room>(`${rpath}/enter?bounded=true`, {});
                setRoom(nextRoom);
                if (route.gameId) {
                    let nextGame = await api<Game>(gpath);
                    if (
                        guest &&
                        nextRoom.admitted &&
                        !nextGame.admitted &&
                        nextGame.permissions?.enter !== false
                    )
                        nextGame = await api<Game>(`${gpath}/enter`, {});
                    setGame(nextGame);
                }
            } else if (route.houseId)
                setHouse(await api<House>(`${housePath(route.houseId)}?bounded=true`));
            setHome(await api<WorkspaceHome>("/browse/home"));
            const catalogReply = await catalogPromise;
            if ("error" in catalogReply) throw catalogReply.error;
            setCatalog(catalogReply.value.items);
            setOnline(true);
        } catch (err) {
            setOnline(false);
            if (loading)
                setError(
                    err instanceof Error
                        ? err.message
                        : "Connection interrupted. Try again.",
                );
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void refresh();
        const timer = setInterval(() => void refresh(), 4000);
        return () => clearInterval(timer);
    }, [route.roomId, route.gameId, route.houseId, guest?.id]);
    useEffect(() => {
        const consume = () => {
            if (route.page === "play") {
                const next = takeCredential(
                    window.location,
                    window.history,
                    undefined,
                    false,
                );
                if (next) setCredential(next);
            }
        };
        window.addEventListener("hashchange", consume);
        return () => window.removeEventListener("hashchange", consume);
    }, [route.page]);
    useEffect(() => {
        document.title = `${game?.name ?? room?.name ?? house?.name ?? "The Guildhall"} · Board Game Creator`;
    }, [game?.name, room?.name, house?.name]);
    const act = async (work: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        try {
            await work();
            setRefreshKey(k => k + 1);
            await refresh();
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Connection interrupted. Try again.",
            );
            await refresh();
        } finally {
            setBusy(false);
        }
    };
    const ensureGuest = async () => {
        if (guest) return guest;
        const reply = await api<{ identity: Guest; recoveryToken?: string }>(
            "/session",
            { name: "Guest" },
        );
        onGuest(reply.identity);
        onRecovery(reply.recoveryToken ?? null);
        return reply.identity;
    };
    const openSetup = (id?: string) => {
        setInitialGame(id);
        setModal("setup");
    };
    const launch = async (config: LaunchConfig) => {
        const identity = await ensureGuest();
        const created = await api<Game>(`${rpath}/launch`, config);
        navigate(
            `${gamePath(created.roomId, created.id)}${created.status === "running" && created.seats.some((s) => s.identityId === identity.id) ? "/play" : ""}`,
        );
    };
    const mutate = (suffix: string, body: object = {}) =>
        act(async () => {
            let result: Game;
            try {
                result = await api<Game>(`${gpath}/${suffix}`, {
                    expectedRevision: game?.revision,
                    ...body,
                });
            } catch (error) {
                if (suffix === "rematch" && error instanceof Error && error.message === "unknown_variant") {
                    setRematchPresetMissingFor(game?.id ?? null);
                    throw new Error("The current release does not contain this table's preset. Choose a current setup to create a new table.");
                }
                throw error;
            }
            if (suffix === "rematch")
                navigate(gamePath(result.roomId, result.id));
            else setGame(result);
        });
    const copy = async (path: string) => {
        try {
            await navigator.clipboard.writeText(
                new URL(path, window.location.origin).href,
            );
            setNotice("Invitation link copied.");
        } catch {
            setNotice(new URL(path, window.location.origin).href);
        }
    };
    const [botSeat, setBotSeat] = useState<string | null>(null);
    const [botDraft, setBotDraft] = useState<BotChoice | null>(null);
    // The bot types this table's game offers; with only the house bot the one-click button stays.
    const botTypes = selectableBots(
        catalog.find((c) => c.id === game?.catalogId)?.variants.find((v) => v.id === game?.variantId)?.bots,
    );
    // Table layouts a player link can open (`?ui=<id>`); "" keeps the game's default.
    const layouts = catalog.find((c) => c.id === game?.catalogId)?.presentationVariants ?? [];
    const [linkLayout, setLinkLayout] = useState("");
    const chosenLayout = layouts.some((l) => l.id === linkLayout) ? linkLayout : "";
    const roomOwner = room?.permissions?.manage ?? room?.ownerId === guest?.id;
    const gameManager =
        game?.permissions?.manage ?? (roomOwner || game?.hostId === guest?.id);
    const canCreate = room?.permissions?.create ?? room?.admitted;
    const ownSeat = game?.seats.find(
        (s) => s.identityId === guest?.id && !s.bot,
    );
    const gameTitle =
        catalog.find((c) => c.id === game?.catalogId)?.title ??
        game?.catalogId ??
        "";
    const gameSetupSummary = game?.setupManifest?.independentPlayerCount
        ? [
              `${game.seats.length} ${game.seats.length === 1 ? "player" : "players"}`,
              ...enabledSettings(game.setupManifest.settings, game.setupValues ?? {}, game.seats.length)
                  .filter(setting => setting.type === "enum")
                  .flatMap(setting => {
                      const value = game.setupValues?.[setting.key] ?? setting.default;
                      const label = setting.options?.find(option => (option.value ?? option.id) === value)?.label;
                      return label ? [label] : [];
                  }),
          ].join(" · ")
        : game?.variantId;
    const favorites = home?.favorites ?? [];
    useEffect(() => {
        const selected = new URLSearchParams(window.location.search).get(
            "setup",
        );
        if (room && catalog.length && new URLSearchParams(window.location.search).has("setup") && modal === null) {
            setInitialGame(selected || undefined);
            setModal("setup");
            window.history.replaceState(null, "", window.location.pathname);
        }
    }, [room?.id, catalog.length]);
    const favorite = (id: string) =>
        act(async () => {
            const ids = favorites.includes(id)
                ? favorites.filter((x) => x !== id)
                : [...favorites, id];
            await api("/favorites", { catalogIds: ids });
        });
    const headerTitle = game?.name ?? room?.name ?? house?.name ?? ({ games:"My games", rooms:"Rooms", houses:"Houses", archive:"Archive", catalog:"Game catalog", favorites:"Favorite games", invitations:"Invitations" })[workspaceView];
    const openFavorite = (id: string) => { setInitialGame(id); setModal("favorite-room"); };
    return (
        <div className={`guildhall workspace ${route.page === "play" || route.page === "watch" ? "workspace-in-game" : ""}`}>
            <a className="guild-skip" href="#guild-main">
                Skip to content
            </a>
            <header className="workspace-top">
                <a className="workspace-brand" href="/"><span aria-hidden="true">◈</span>Guild</a>
                <span className="workspace-tagline">A place to play together</span>
                <div className="workspace-top-actions"><IssueReporter room={room?.admitted ? room : null} game={room?.admitted ? game : null} workspaceView={workspaceView} /><a className="workspace-inbox" href="/?view=invitations" aria-label={`Invitations, ${home?.counts?.invitations ?? 0} pending`}>✉</a><button type="button" className="guild-profile-button" aria-label={guest?.registered ? undefined : "Join the guild"} onClick={() => setModal("account")}><Avatar profile={guest?.profile} name={guest?.name ?? "Guest"} /><span>{guest?.profile?.displayName ?? guest?.name ?? "Guest"}</span></button></div>
            </header>
            <WorkspaceSidebar view={workspaceView} home={home} guest={guest} openAccount={() => setModal("account")} openFavorite={openFavorite} newRoom={guest?.canCreateRooms && (!house || house.permissions.manage) ? () => setModal("room") : undefined} />
            <div className={`workspace-content ${route.page === "play" || route.page === "watch" ? "workspace-playing" : ""}`}>
            <div className="workspace-heading"><div>{(room || house || game) && <a className="workspace-breadcrumb" href={game ? rpath : "/?view=rooms"}>{game ? room?.name : "Workspace"} /</a>}<h1>{headerTitle}</h1></div><div className="guild-actions">
                {!game && room && canCreate && <button type="button" className="guild-btn primary" onClick={() => openSetup()}>＋ Set up a game</button>}
                {!game && !room && guest?.canCreateRooms && (house?.permissions.manage || (!house && workspaceView === "rooms")) && <button type="button" className="guild-btn primary" onClick={() => setModal("room")}>＋ Make a room</button>}
                {!game && !room && !house && ["games","archive","catalog","favorites"].includes(workspaceView) && <button type="button" className="guild-btn primary" onClick={() => { setInitialGame(undefined); setModal("favorite-room"); }}>＋ New game</button>}
            </div></div>
            {(error || notice || !online) && (
                <div
                    className={`guild-message ${error ? "error" : ""}`}
                    role={error ? "alert" : "status"}
                    data-issue-private
                >
                    {error ??
                        notice ??
                        "Reconnecting to the host. Your saved positions are safe."}
                    <button
                        type="button"
                        aria-label="Dismiss message"
                        onClick={() => {
                            setError(null);
                            setNotice(null);
                        }}
                    >
                        ×
                    </button>
                </div>
            )}
            {loading ? (
                <main id="guild-main" className="guild-main">
                    <p role="status">Loading workspace…</p>
                </main>
            ) : (route.page === "play" || route.page === "watch") &&
              game &&
              !game.unavailable &&
              game.status !== "setup" ? (
                <main id="guild-main" className="guild-live">
                    <div className="guild-player-ribbon">
                        <a href={gpath}>← Your table</a>
                        <span>
                            {route.page === "watch"
                                ? "Watching · public view"
                                : "Your player view"}
                        </span>
                        <div
                            className={`guild-live-profiles ${game.seats.length > 6 ? "many-profiles" : ""}`}
                        >
                            {game.seats.map((s) => (
                                <span key={s.id}>
                                    <Avatar
                                        profile={s.profile}
                                        name={s.name ?? s.id}
                                        bot={s.bot}
                                    />
                                    {s.profile?.displayName ?? s.name ?? s.id}
                                </span>
                            ))}
                        </div>
                    </div>
                    <PlayerGate
                        game={game}
                        spectator={route.page === "watch"}
                        fragment={credential}
                    />
                    {game.notifications?.slice(-1).map((n) => (
                        <p
                            className="guild-message"
                            role="status"
                            key={n.revision}
                        >
                            {n.message}
                        </p>
                    ))}
                </main>
            ) : game ? (
                <main id="guild-main" className="guild-main">
                    <div className="guild-toolbar">
                        <a href={rpath}>← {room?.name}</a>
                        <span className={`guild-status status-${game.status}`}>
                            {game.unavailable
                                ? "Unavailable"
                                : game.status === "setup"
                                ? "Waiting for players"
                                : game.status}
                        </span>
                        {gameManager && (
                            <button
                                type="button"
                                className="guild-link"
                                onClick={() => setModal("game-settings")}
                            >
                                Table settings
                            </button>
                        )}
                    </div>
                    {game.unavailable && (
                        <p role="status" className="guild-message">
                            This saved game cannot be loaded by the current package. Its data is preserved, but play and review are unavailable for now.
                        </p>
                    )}
                    <div className="guild-table-page">
                        <section>
                            <div
                                className="workspace-player-table"
                            >
                                <div className="workspace-table-summary">
                                    <GameShield
                                        game={{
                                            id: game.catalogId,
                                            title: gameTitle,
                                        }}
                                    />
                                    <h2>{game.name}</h2>
                                    <p>
                                        {gameTitle} · {gameSetupSummary}
                                    </p>
                                </div>
                                <div className="workspace-player-rows">
                                    {game.seats.map((s, i) => {
                                        const mine = s.identityId === guest?.id;
                                        const policies =
                                            game.policies ?? defaultPolicies;
                                        return (
                                            <article
                                                key={s.id}
                                                className={`guild-stool workspace-player-row ${s.ready || s.bot ? "ready" : ""}`}
                                            >
                                                <Avatar
                                                    large
                                                    profile={s.profile}
                                                    name={
                                                        s.profile
                                                            ?.displayName ??
                                                        s.name ??
                                                        `Seat ${i + 1}`
                                                    }
                                                    bot={s.bot}
                                                    open={
                                                        !s.bot && !s.identityId
                                                    }
                                                />
                                                <h3>
                                                    {s.profile?.displayName ??
                                                        s.name ??
                                                        (s.bot
                                                            ? s.botProfile
                                                                ? `${s.botProfile.label} bot`
                                                                : "House bot"
                                                            : "Open seat")}
                                                </h3>
                                                <p
                                                    title={
                                                        s.botProfile
                                                            ? describeBotParams(
                                                                  botTypes.find((t) => t.id === s.botProfile?.type),
                                                                  s.botProfile.params,
                                                              ).join(", ")
                                                            : undefined
                                                    }
                                                >
                                                    {s.bot
                                                        ? botSubtitle(s.botProfile)
                                                        : s.identityId
                                                          ? `${s.ready ? "Ready ✓" : "Not ready"}${mine ? " · you" : ""}`
                                                          : `Seat ${i + 1}`}
                                                </p>
                                                {!game.archived &&
                                                    game.status !==
                                                        "finished" && (
                                                        <>
                                                            {!mine &&
                                                                ((!s.identityId &&
                                                                    !s.bot) ||
                                                                    (game
                                                                        .permissions
                                                                        ?.replaceHuman ??
                                                                        (gameManager &&
                                                                            policies.humanReplacement))) && (
                                                                    <button
                                                                        type="button"
                                                                        className="guild-btn small"
                                                                        disabled={
                                                                            busy
                                                                        }
                                                                        onClick={() =>
                                                                            void mutate(
                                                                                `seats/${s.id}/claim`,
                                                                                {
                                                                                    name:
                                                                                        guest
                                                                                            ?.profile
                                                                                            ?.displayName ??
                                                                                        guest?.name,
                                                                                },
                                                                            )
                                                                        }
                                                                    >
                                                                        {s.bot
                                                                            ? "Replace bot"
                                                                            : s.identityId
                                                                              ? "Replace player"
                                                                              : "Take this seat"}
                                                                    </button>
                                                                )}
                                                            {(mine ||
                                                                (game
                                                                    .permissions
                                                                    ?.releaseOthers ??
                                                                    gameManager)) &&
                                                                (s.identityId ||
                                                                    s.bot) && (
                                                                    <button
                                                                        type="button"
                                                                        className="guild-link"
                                                                        disabled={
                                                                            busy
                                                                        }
                                                                        onClick={() =>
                                                                            void mutate(
                                                                                `seats/${s.id}/release`,
                                                                            )
                                                                        }
                                                                    >
                                                                        {mine
                                                                            ? "Leave seat"
                                                                            : "Clear seat"}
                                                                    </button>
                                                                )}
                                                            {!s.bot &&
                                                                (game
                                                                    .permissions
                                                                    ?.replaceBot ??
                                                                    ((gameManager ||
                                                                        mine) &&
                                                                        (game.status ===
                                                                            "setup" ||
                                                                            policies.botReplacement))) &&
                                                                catalog
                                                                    .find(
                                                                        (c) =>
                                                                            c.id ===
                                                                            game.catalogId,
                                                                    )
                                                                    ?.variants.find(
                                                                        (v) =>
                                                                            v.id ===
                                                                            game.variantId,
                                                                    )
                                                                    ?.botsSupported && (
                                                                    <button
                                                                        type="button"
                                                                        className="guild-link"
                                                                        disabled={
                                                                            busy
                                                                        }
                                                                        aria-expanded={botTypes.length > 1 ? botSeat === s.id : undefined}
                                                                        onClick={() => {
                                                                            if (botTypes.length > 1) {
                                                                                setBotDraft(null);
                                                                                setBotSeat(botSeat === s.id ? null : s.id);
                                                                            } else
                                                                                void mutate(
                                                                                    `seats/${s.id}/bot`,
                                                                                    {
                                                                                        bot: true,
                                                                                    },
                                                                                );
                                                                        }}
                                                                    >
                                                                        {s.identityId
                                                                            ? "Replace with bot"
                                                                            : "Add bot"}
                                                                    </button>
                                                                )}
                                                            {s.bot &&
                                                                botTypes.length > 1 &&
                                                                (game.permissions?.replaceBot ??
                                                                    ((gameManager || mine) &&
                                                                        (game.status === "setup" || policies.botReplacement))) && (
                                                                    <button
                                                                        type="button"
                                                                        className="guild-link"
                                                                        disabled={busy}
                                                                        aria-expanded={botSeat === s.id}
                                                                        onClick={() => {
                                                                            const current = s.botProfile;
                                                                            setBotDraft(current ? { type: current.type, version: current.version, params: current.params } : null);
                                                                            setBotSeat(botSeat === s.id ? null : s.id);
                                                                        }}
                                                                    >
                                                                        Change bot
                                                                    </button>
                                                                )}
                                                            {botSeat === s.id && botTypes.length > 1 && (
                                                                <div className="guild-bot-popover" role="group" aria-label={`Choose a bot for seat ${i + 1}`}>
                                                                    <BotTypeFields types={botTypes} choice={botDraft} onChange={setBotDraft} />
                                                                    <div className="guild-actions">
                                                                        <button
                                                                            type="button"
                                                                            className="guild-btn small"
                                                                            disabled={busy || !botChoiceValid(botTypes, botDraft)}
                                                                            onClick={() => {
                                                                                void mutate(`seats/${s.id}/bot`, { bot: botRequest(botTypes, botDraft) ?? true });
                                                                                setBotSeat(null);
                                                                            }}
                                                                        >
                                                                            Seat this bot
                                                                        </button>
                                                                        <button type="button" className="guild-link" onClick={() => setBotSeat(null)}>
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {mine &&
                                                                game.status ===
                                                                    "setup" && (
                                                                    <button
                                                                        type="button"
                                                                        className="guild-btn"
                                                                        aria-pressed={
                                                                            s.ready
                                                                        }
                                                                        disabled={
                                                                            busy
                                                                        }
                                                                        onClick={() =>
                                                                            void mutate(
                                                                                `seats/${s.id}/ready`,
                                                                                {
                                                                                    ready: !s.ready,
                                                                                },
                                                                            )
                                                                        }
                                                                    >
                                                                        {s.ready
                                                                            ? "✓ I’m ready"
                                                                            : "I’m ready"}
                                                                    </button>
                                                                )}
                                                        </>
                                                    )}
                                            </article>
                                        );
                                    })}
                                </div>
                            </div>
                            {game.status === "finished" && (
                                <PublicResult game={game} />
                            )}
                            {!game.unavailable && game.permissions?.review && (
                                <PerspectiveReview game={game} />
                            )}
                            {game.notifications?.length ? (
                                <section className="guild-panel">
                                    <h3>Table notices</h3>
                                    {game.notifications.slice(-8).map((n) => (
                                        <p key={`${n.revision}-${n.at}`}>
                                            {n.message}
                                        </p>
                                    ))}
                                </section>
                            ) : null}
                        </section>
                        <aside className="guild-panel guild-party">
                            <h2>
                                {game.status === "setup"
                                    ? "Your party"
                                    : "Your table"}
                            </h2>
                            {game.status === "setup" && (
                                <p>
                                    {
                                        game.seats.filter(
                                            (s) => s.bot || s.ready,
                                        ).length
                                    }{" "}
                                    of {game.seats.length} seats ready
                                </p>
                            )}
                            {game.unavailable ? null : game.status === "setup" ? (
                                <>
                                    <button
                                        type="button"
                                        className="guild-btn primary"
                                        aria-label="Set out"
                                        disabled={
                                            busy ||
                                            game.permissions?.start === false ||
                                            game.seats.some(
                                                (s) =>
                                                    !s.bot &&
                                                    (!s.identityId || !s.ready),
                                            )
                                        }
                                        onClick={() => void mutate("start")}
                                    >
                                            <span>Set out</span>

                                    </button>
                                    <p className="guild-hint">
                                        Every human claims and readies their own
                                        chair.{" "}
                                        {game.permissions?.start === false
                                            ? "The room charter limits who can start."
                                            : "When the party is ready, open the door."}
                                    </p>
                                </>
                            ) : (
                                <>
                                    {ownSeat && (
                                        <>
                                            {layouts.length >= 2 && (
                                                <Field label="Layout">
                                                    <select
                                                        value={chosenLayout}
                                                        aria-label="Layout for your player link"
                                                        onChange={(e) => setLinkLayout(e.target.value)}
                                                    >
                                                        <option value="">Game default</option>
                                                        {layouts.map((l) => (
                                                            <option key={l.id} value={l.id}>
                                                                {l.label}
                                                                {l.formFactor === "phone" ? " · phone" : l.formFactor === "desktop" ? " · large screen" : ""}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </Field>
                                            )}
                                            <a
                                                className="guild-btn primary"
                                                href={withLayout(`${gpath}/play`, chosenLayout || null)}
                                            >
                                                Continue playing →
                                            </a>
                                            <button
                                                type="button"
                                                className="guild-link"
                                                disabled={busy}
                                                onClick={() =>
                                                    void act(async () => {
                                                        const link = await api<{
                                                            url: string;
                                                        }>(
                                                            `${gpath}/player-link`,
                                                        );
                                                        try {
                                                            await navigator.clipboard.writeText(
                                                                new URL(
                                                                    withLayout(link.url, chosenLayout || null),
                                                                    window.location.origin,
                                                                ).href,
                                                            );
                                                            setNotice(
                                                                "Your private player link is copied. Keep it for yourself.",
                                                            );
                                                        } catch {
                                                            setNotice(
                                                                "Clipboard unavailable. Your own seat remains available through this account or guest identity.",
                                                            );
                                                        }
                                                    })
                                                }
                                            >
                                                Copy my private player link
                                            </button>
                                        </>
                                    )}
                                    {game.policies?.liveSpectating !==
                                        false && (
                                        <a
                                            className="guild-btn"
                                            href={`${gpath}/watch`}
                                        >
                                            Watch the table
                                        </a>
                                    )}
                                    {gameManager &&
                                        !game.archived &&
                                        game.status !== "finished" && (
                                            <button
                                                type="button"
                                                className="guild-btn"
                                                disabled={busy}
                                                onClick={() =>
                                                    void mutate(
                                                        game.status === "paused"
                                                            ? "resume"
                                                            : "pause",
                                                    )
                                                }
                                            >
                                                {game.status === "paused"
                                                    ? "Resume game"
                                                    : "Pause game"}
                                            </button>
                                        )}
                                    {gameManager && (
                                        <button
                                            type="button"
                                            className="guild-btn"
                                            disabled={busy}
                                            onClick={() =>
                                                void mutate("rematch")
                                            }
                                        >
                                            ↻ Rematch
                                        </button>
                                    )}
                                    {rematchPresetMissingFor === game.id && canCreate && (
                                        <button type="button" className="guild-btn" disabled={busy} onClick={() => openSetup(game.catalogId)}>
                                            Set up a new current table
                                        </button>
                                    )}
                                </>
                            )}
                            {gameManager && !game.unavailable && (
                                <button
                                    type="button"
                                    className="guild-link"
                                    disabled={busy}
                                    onClick={() =>
                                        void mutate(
                                            game.archived
                                                ? "restore"
                                                : "archive",
                                        )
                                    }
                                >
                                    {game.archived
                                        ? "Restore table (stays paused)"
                                        : "Put away in archive"}
                                </button>
                            )}
                            {game.botStalled?.length > 0 && (
                                <p role="status">
                                    A bot is waiting for the host’s
                                    attention. The saved game is intact.
                                    {game.botStalled.some((id) => game.seats.find((seat) => seat.id === id)?.botProfile) &&
                                        " A bot type may be unavailable in this game version; replace it or change its type."}
                                </p>
                            )}
                            {game.status !== "finished" && (
                                <p className="guild-hint">
                                    Putting away an unfinished game pauses play.
                                    It does not complete the game or reveal
                                    private perspectives.
                                </p>
                            )}
                        </aside>
                    </div>
                </main>
            ) : room ? (
                <main id="guild-main" className="guild-main workspace-directory"><div className="guild-toolbar"><nav aria-label="Room sections"><a href={rpath} className={route.page === "room" ? "active" : ""}>The tables</a><a href={`${rpath}/archive`} className={route.page === "archive" ? "active" : ""}>Archive</a></nav><div className="guild-actions"><button type="button" className="guild-link" onClick={() => void copy(rpath)}>Invite by link ↗</button>{roomOwner && <button type="button" className="guild-link" onClick={() => setModal("room-settings")}>Room charter</button>}</div></div>
                    {route.page !== "archive" && <div className="workspace-pins" aria-label="Pinned games">{room.pins?.map(id => <button type="button" className="guild-btn small" key={id} disabled={!canCreate || !gameAllowed(room,id)} onClick={() => openSetup(id)}>{catalog.find(item => item.id === id)?.title ?? id}</button>)}{canCreate && <button type="button" className="guild-btn small" onClick={() => openSetup()}>All games</button>}{!canCreate && <span className="guild-muted">The room charter restricts table creation.</span>}</div>}
                    <WorkspaceBrowser key={route.page} view={route.page === "archive" ? "archive" : "games"} room={room} guest={guest} favorites={favorites} toggleFavorite={favorite} openFavorite={openFavorite} refreshKey={refreshKey}/>
                </main>
            ) : house ? (
                <main id="guild-main" className="guild-main workspace-directory"><div className="guild-toolbar"><span>{house.listed ? "Listed publicly" : "Unlisted"} · {house.entryPolicy.mode}</span>{house.permissions.manage && <div className="guild-actions"><button type="button" className="guild-btn" onClick={() => setModal("invite")}>Invite someone over</button><button type="button" className="guild-link" onClick={() => setModal("house-settings")}>House charter</button></div>}{house.invited && !house.member && <button type="button" className="guild-btn primary" disabled={busy} onClick={() => void act(async () => { setHouse(await api<House>(`${housePath(house.id)}/accept?bounded=true`,{})); })}>Accept house invitation</button>}</div>
                    {house.permissions.enter ? <><WorkspaceBrowser view="rooms" house={house} guest={guest} favorites={favorites} toggleFavorite={favorite} openFavorite={openFavorite} refreshKey={refreshKey}/>{house.permissions.manage && <HouseConnections house={house} busy={busy} act={act}/>}</> : <Empty title="This house has a guest list." text="The owner has restricted entry. Listing a house does not grant access to its rooms."/>}
                </main>
            ) : (
                <main id="guild-main" className="guild-main workspace-directory"><WorkspaceBrowser key={workspaceView} view={workspaceView} guest={guest} favorites={favorites} toggleFavorite={favorite} openFavorite={openFavorite} refreshKey={refreshKey}/></main>
            )}
            <footer className="guild-footer">
                <span>{online ? "Connected to the host" : "Reconnecting…"}</span>
                <span>Public rooms · Guests welcome</span>
            </footer>
            </div>
            {modal === "favorite-room" && (
                <Dialog
                    title="Where shall we gather?"
                    close={() => setModal(null)}
                >
                    <p>
                        Choose a room for{" "}
                        {catalog.find((c) => c.id === initialGame)?.title}.
                    </p>
                    <RoomPicker gameId={initialGame} />
                    <p className="guild-hint">
                        The room charter decides whether you may create and
                        start this game.
                    </p>
                </Dialog>
            )}
            {modal === "account" && (
                <Dialog
                    title={
                        guest?.registered
                            ? "Your guild profile"
                            : "Join the guild"
                    }
                    close={() => setModal(null)}
                >
                    <AccountPanel
                        guest={guest}
                        busy={busy}
                        act={act}
                        update={onGuest}
                        recovery={recovery}
                        keepRecovery={onRecovery}
                    />
                </Dialog>
            )}
            {modal === "room" && (
                <Dialog title="Make a room" close={() => setModal(null)}>
                    <form
                        className="guild-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void act(async () => {
                                const created = await api<Room>("/rooms?bounded=true", {
                                    name: roomName,
                                    theme: "clubhouse",
                                    ...(house ? { houseId: house.id } : {}),
                                });
                                navigate(roomPath(created.id));
                            });
                        }}
                    >
                        <RoomNameBuilder onName={setRoomName} />
                        <p>
                            Listed publicly. Open to everyone through your
                            house’s door. You can change its charter after
                            creation.
                        </p>
                        <button
                            className="guild-btn primary"
                            disabled={busy || !roomName.trim()}
                        >
                            Create room →
                        </button>
                    </form>
                </Dialog>
            )}
            {modal === "setup" && room && (
                <Dialog
                    title="Set up a game"
                    wide
                    close={() => setModal(null)}
                >
                    <GameSetup
                        catalog={catalog}
                        room={room}
                        guest={guest}
                        initial={initialGame}
                        busy={busy}
                        launch={(config) => act(() => launch(config))}
                    />
                </Dialog>
            )}
            {modal === "room-settings" && room && (
                <Dialog title="Room charter" wide close={() => setModal(null)}>
                    <RoomCharter
                        room={room}
                        catalog={catalog}
                        busy={busy}
                        save={(body) =>
                            act(async () => {
                                setRoom(
                                    await api<Room>(`${rpath}/settings?bounded=true`, body),
                                );
                                setNotice(
                                    "Room charter saved. Future tables use these defaults.",
                                );
                                setModal(null);
                            })
                        }
                    />
                </Dialog>
            )}
            {modal === "house-settings" && house && (
                <Dialog title="House charter" close={() => setModal(null)}>
                    <HouseCharter
                        house={house}
                        busy={busy}
                        save={(body) =>
                            act(async () => {
                                setHouse(
                                    await api<House>(
                                        `${housePath(house.id)}/settings?bounded=true`,
                                        body,
                                    ),
                                );
                                setModal(null);
                            })
                        }
                    />
                </Dialog>
            )}
            {modal === "game-settings" && game && (
                <Dialog title="Table settings" close={() => setModal(null)}>
                    <GameCharter game={game} busy={busy} save={mutate} />
                </Dialog>
            )}
            {modal === "invite" && house && (
                <Dialog
                    title="Invite someone over"
                    close={() => setModal(null)}
                >
                    <form
                        className="guild-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            const loginName = new FormData(e.currentTarget).get(
                                "loginName",
                            );
                            void act(async () => {
                                await api(
                                    `${housePath(house.id)}/invitations`,
                                    { loginName },
                                );
                                setModal(null);
                            });
                        }}
                    >
                        <p>
                            The invitation connects this house to their Home
                            screen. House and room restrictions still apply.
                        </p>
                        <Field label="Their login name">
                            <input
                                name="loginName"
                                autoComplete="off"
                                required
                            />
                        </Field>
                        <button className="guild-btn primary" disabled={busy}>
                            Invite over
                        </button>
                    </form>
                </Dialog>
            )}
        </div>
    );
}
function gameAllowed(room: Room, id: string) {
    return room.gamePolicy?.mode === "allowlist"
        ? room.gamePolicy.catalogIds.includes(id)
        : room.gamePolicy?.mode === "denylist"
          ? !room.gamePolicy.catalogIds.includes(id)
          : true;
}
function HouseBanner({ house }: { house: House }) {
    return (
        <a className="guild-house-banner" href={housePath(house.id)}>
            <span className="guild-banner-shield" aria-hidden="true">
                ⚔
            </span>
            <div>
                <h3>{house.name}</h3>
                <p>
                    {house.invited && !house.member
                        ? "Invitation waiting"
                        : house.permissions.enter
                          ? `${house.rooms?.length ?? 0} rooms · come on in`
                          : "Entry restricted"}
                </p>
            </div>
            <span aria-hidden="true">↗</span>
        </a>
    );
}
function RoomCard({ room }: { room: Room }) {
    return (
        <a className="guild-room-card" href={roomPath(room.id)}>
            <span aria-hidden="true">♜</span>
            <div>
                <h3>{room.name}</h3>
                <p>
                    {room.permissions?.enter === false
                        ? "Entry restricted"
                        : `${room.games?.filter((g) => !g.archived).length ?? 0} tables · come on in`}
                </p>
            </div>
            <span aria-hidden="true">↗</span>
        </a>
    );
}
function Empty({ title, text }: { title: string; text: string }) {
    return (
        <div className="guild-empty">
            <h2>{title}</h2>
            <p>{text}</p>
        </div>
    );
}
function GameCard({
    game,
    catalog,
    guest,
    parchment = false,
}: {
    game: Game;
    catalog: CatalogGame[];
    guest: Guest | null;
    parchment?: boolean;
}) {
    const c = catalog.find((c) => c.id === game.catalogId);
    const mine =
        game.admitted &&
        game.seats.some((s) => s.identityId === guest?.id && !s.bot);
    const path = gamePath(game.roomId, game.id);
    return (
        <article className={`guild-game-card ${parchment ? "parchment" : ""}`}>
            <a className="guild-game-card-main" href={path}>
                <GameShield
                    game={{
                        id: game.catalogId,
                        title: c?.title ?? game.catalogId,
                    }}
                />
                <div>
                    <span className="guild-kicker">
                        {c?.title ?? game.catalogId}
                    </span>
                    <h3>{game.name}</h3>
                    <p>
                        {game.unavailable
                            ? "This saved game cannot be loaded by the current package. Its data is preserved."
                            : game.status === "setup"
                            ? `${game.seats.filter((s) => !s.identityId && !s.bot).length} open seats · gathering the party`
                            : game.status === "paused"
                              ? "Paused · position saved"
                              : game.status === "finished"
                                ? "Finished · a tale for the archive"
                                : "Adventure in progress"}
                    </p>
                    {game.completedAt && (
                        <time dateTime={game.completedAt}>
                            {new Date(game.completedAt).toLocaleDateString()}
                        </time>
                    )}
                    {game.status === "finished" && game.result != null && (
                        <small className="guild-result-summary">
                            {resultSummary(game)}
                        </small>
                    )}
                    <div className="guild-avatar-stack">
                        {game.seats.map((s) => (
                            <Avatar
                                key={s.id}
                                profile={s.profile}
                                name={
                                    s.profile?.displayName ??
                                    s.name ??
                                    "Open seat"
                                }
                                bot={s.bot}
                                open={!s.identityId && !s.bot}
                            />
                        ))}
                    </div>
                </div>
            </a>
            <div className="guild-game-card-foot">
                <span className={`guild-status status-${game.status}`}>
                    {game.unavailable ? "Unavailable" : game.status === "setup" ? "Ready your party" : game.status}
                </span>
                <a
                    href={`${path}${game.unavailable || game.status === "setup" || game.archived ? "" : mine ? "/play" : "/watch"}`}
                >
                    {game.unavailable
                        ? "View details"
                        : game.status === "setup"
                        ? "Choose a chair"
                        : game.archived
                          ? "Open tale"
                          : mine
                            ? "Continue"
                            : "Watch"}{" "}
                    →
                </a>
            </div>
        </article>
    );
}
